<template>
    <div class="explosion">
        <div class="explosion-wrapper">
            <div
                class="sprite-container"
                :key="containerIndex"
                v-for="(container, containerIndex) in spriteContainers"
            >
                <div
                    :id="sprite.key"
                    class="sprite"
                    :key="spriteIndex"
                    v-for="(sprite, spriteIndex) in container.list"
                ></div>
            </div>
        </div>
    </div>
</template>

<script>
import gsap from 'gsap'
import Physics2DPlugin from '@/assets/js/Physics2DPlugin.js'

gsap.registerPlugin(Physics2DPlugin)

export default {
    props: {
        size: {
            type: Number,
            default: 100,
        },
        explosionCount: {
            type: Number,
            default: 4,
        },
        spriteCount: {
            type: Number,
            default: 35,
        },
        spriteSizesMin: {
            type: Number,
            default: 5,
        },
        spriteSizesMax: {
            type: Number,
            default: 15,
        },
        speed: {
            type: Number,
            default: 2.4,
        },
        gravity: {
            type: Number,
            default: 0.7,
        },
        delay: {
            type: Number,
            default: 250,
        },
        color: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            spriteContainers: [],
        }
    },

    computed: {
        timeline() {
            let timelines = []

            for (let i = 0; i < this.explosionCount; i++) {
                timelines.push(gsap.timeline({ paused: true }))
            }

            return timelines
        }
    },

    mounted() {
        this.createSpriteContainers()

        this.$nextTick(() => {
            this.setTimeline()
            this.playAnimation()
        })
    },

    methods: {
        // CREATE EXPLOSION SPRITE
        createSpriteContainers() {
            for (let i = 0; i < this.explosionCount; i++) {
                this.spriteContainers.push({
                    key: `explosion-${i}`,
                    list: this.createSprites(i)
                })
            }
        },

        createSprites(containerIndex) {
            let sprites = []
            let dimensions
            let angle
            let length
            let duration
            let color

            for (let i = 0; i < this.spriteCount; i++) {
                dimensions = this.getDimensions()
                // angle = this.getRandom(0.65, 0.85) * Math.PI * 2
                angle = this.getRandom(0.62, 0.87) * Math.PI * 2
                // angle = this.getRandom(0.5, 1) * Math.PI * 2
                length = Math.random() * (this.size / 2 - dimensions / 2)
                duration = 3 + Math.random()
                color = this.color ? this.color : this.getColor()

                sprites.push({
                    key: `explosion-${containerIndex}-sprite-${i}`,
                    dimensions: dimensions,
                    angle: angle,
                    length: length,
                    color: color,
                    duration: duration
                })
            }

            return sprites
        },

        // SET TIMELINE ANIMATION
        setTimeline() {
            let i = 0

            this.spriteContainers.forEach(explosion => {
                explosion.list.forEach(sprite => {
                    gsap.set(`#${sprite.key}`, {
                        backgroundColor: sprite.color,
                        x: Math.cos(sprite.angle) * sprite.length,
                        y: Math.sin(sprite.angle) * sprite.length,
                        width: sprite.dimensions,
                        height: sprite.dimensions,
                        xPercent: -50,
                        yPercent: -50,
                        visibility: 'hidden',
                        force3D: true
                    })

                    this.buildTimeline(i, sprite.key, sprite.duration, sprite.angle)
                })

                i++
            })
        },

        buildTimeline(index, key, duration, angleX) {
            const halfDuration = duration / 2
            const rotationX = this.getRandom(720, 1440)
            const rotationZ = this.getRandom(720, 1440)
            const velocity = (100 + Math.random() * 250) * this.speed
            const gravity = 800 * this.gravity
            const angle = angleX * 180 / Math.PI
            const friction = this.getRandom(0.1, 0.15)

            this.timeline[index]
                .to(`#${key}`, halfDuration, {
                    opacity: 0,
                    ease: 'rough({ template: none.out, strength: 1.75, points: 20, taper: "none", randomize: true, clamp: true})',
                }, 0)
                .to(`#${key}`, duration, {
                    visibility: 'visible',
                    rotationX: `-=${rotationX}`,
                    rotationZ: `+=${rotationZ}`,
                    physics2D: {
                        angle, // translate radians to degrees
                        velocity, // initial velocity
                        gravity,
                        friction
                    }
                }, 0)
                .to(`#${key}`, 1.25 + Math.random(), {
                    opacity: 0
                }, halfDuration)
        },

        playAnimation() {
            let intervalCount = 0

            const interval = setInterval(() => {
                if (intervalCount < this.explosionCount) {
                    this.timeline[intervalCount].restart()

                    intervalCount++;
                } else {
                    clearInterval(interval);
                }
            }, this.delay)
        },

        // GENERATE DATA
        getRandom(min, max) {
            return min + Math.random() * (max - min);
        },

        getDimensions() {
            return this.getRandom(this.spriteSizesMin, this.spriteSizesMax)
        },

        getColor() {
            const r = this.getRandom(30, 255)
            const g = this.getRandom(30, 230)
            const b = this.getRandom(30, 230)

            return `rgb(${r}, ${g}, ${b})`
        },
    }
}
</script>

<style lang="scss" scoped>
.explosion {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    .explosion-wrapper {
        width: 100%;
        height: 100%;

        .emitter {
            position: absolute;
            visibility: hidden;
            background-color: black;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            top: 50%;
            left: 50%;
            transform: translateX(-10px) translateY(-10px);
        }

        .sprite-container {
            position: absolute;
            left: 50%;
            top: 110%;
            // transform: translate(-50%, -50%);
            overflow: visible;
            z-index: 5000;
            pointer-events: none;

            .sprite {
                position: absolute;
                pointer-events: none;
            }
        }
    }
}
</style>